import { SnapshotBucketQueryKey } from 'app/agriculture/queries/snapshots/types/SnapshotBucketQueryKey';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import {
    snapshotConfig,
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { TZDate } from '@date-fns/tz';
import { formatISO } from 'date-fns';
import { handleZodParseError } from 'app/agriculture/utils';
import { groupByKey } from 'app/agriculture/queries/snapshots/utils/groupByKey';
import { SnapshotsMap } from 'app/agriculture/queries/snapshots/types/SnapshotsMap';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';

/**
 * Gets a snapshot latest before, parsed and grouped by parent id
 *
 * Note: bucket timestamp should be standardised to the start of a bucket in order to properly leverage the cache
 */
export async function getSnapshotsLatestBefore<
    T extends SnapshotType,
    I extends Snapshot = SnapshotInterface<T>,
>({
    queryKey: [snapshotType, { farmId, bucketTimestamp }],
}: {
    queryKey: SnapshotBucketQueryKey<T>;
}): Promise<SnapshotsMap<I>> {
    const instance = createAxiosInstance();
    const { endpoint, parseSnapshots } = snapshotConfig[snapshotType];
    const latestBefore = new TZDate(bucketTimestamp, 'UTC');

    const { data } = await instance.get<unknown[]>(`${endpoint}latest_before/`, {
        params: {
            farm_id: farmId,
            latest_before: formatISO(latestBefore),
        },
    });

    try {
        /** parse the snapshots */
        const parsedSnapshots = parseSnapshots(data) as I[];

        /** group snapshots by parentId */
        return groupByKey<I>(parsedSnapshots, 'parentId');
    } catch (e) {
        handleZodParseError(e);
        throw e;
    }
}
